import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";

// API base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_URL;

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: "LOGIN_START",
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILURE: "LOGIN_FAILURE",
  LOGOUT: "LOGOUT",
  REFRESH_TOKEN: "REFRESH_TOKEN",
  CLEAR_ERROR: "CLEAR_ERROR",
  AUTH_CHECK_START: "AUTH_CHECK_START",
  AUTH_CHECK_COMPLETE: "AUTH_CHECK_COMPLETE",
};

// Initial state
const initialState = {
  user: null,
  tokens: {
    accessToken: localStorage.getItem("accessToken") || null,
    // refreshToken: localStorage.getItem('refreshToken') || null
  },
  isAuthenticated: false,
  // Start in loading state so routes wait for initial auth check
  isLoading: true,
  error: null,
  userRole: null,
};

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        tokens: action.payload.tokens,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        userRole: action.payload.user.role,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        tokens: { accessToken: null, refreshToken: null },
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        userRole: null,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        tokens: { accessToken: null, refreshToken: null },
        isAuthenticated: false,
        isLoading: false,
        error: null,
        userRole: null,
      };

    case AUTH_ACTIONS.REFRESH_TOKEN:
      return {
        ...state,
        tokens: action.payload.tokens,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        userRole: action.payload.user?.role || null,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AUTH_ACTIONS.AUTH_CHECK_START:
      return {
        ...state,
        isLoading: true,
      };

    case AUTH_ACTIONS.AUTH_CHECK_COMPLETE:
      return {
        ...state,
        isLoading: false,
      };

    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();
  const hasCheckedAuth = useRef(false);

  // Logout function
  const logout = useCallback(async () => {
    try {
      // Call logout endpoint if token exists
      if (state.tokens.accessToken) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${state.tokens.accessToken}`,
          },
        });
      }
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      // Clear local storage and state
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      navigate("/login");
    }
  }, [state.tokens.accessToken, navigate, dispatch]);

  // Refresh access token
  const refreshAccessToken = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const result = await response.json();

        localStorage.setItem("accessToken", result.data.tokens.accessToken);
        localStorage.setItem("refreshToken", result.data.tokens.refreshToken);

        // Ensure we have the latest user payload; fetch if backend didn't include it
        let refreshedUser = result?.data?.user || null;
        if (!refreshedUser) {
          try {
            const meResponse = await fetch(`${API_BASE_URL}/auth/me`, {
              headers: {
                Authorization: `Bearer ${result.data.tokens.accessToken}`,
              },
            });
            if (meResponse.ok) {
              const meData = await meResponse.json();
              refreshedUser = meData?.data?.user || null;
            }
          } catch (_) {
            // Ignore and proceed; reducer will handle nulls conservatively
          }
        }

        dispatch({
          type: AUTH_ACTIONS.REFRESH_TOKEN,
          payload: {
            tokens: result.data.tokens,
            user: refreshedUser,
          },
        });

        return result.data.tokens.accessToken;
      } else {
        throw new Error("Token refresh failed");
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      logout();
      return null;
    }
  }, [logout, dispatch]);

  // Check if user is authenticated on mount
  useEffect(() => {
    // Prevent duplicate API calls in React Strict Mode
    if (hasCheckedAuth.current) {
      return;
    }

    hasCheckedAuth.current = true;

    const checkAuthStatus = async () => {
      dispatch({ type: AUTH_ACTIONS.AUTH_CHECK_START });
      const accessToken = localStorage.getItem("accessToken");
      const refreshToken = localStorage.getItem("refreshToken");

      if (accessToken && refreshToken) {
        try {
          // Verify token validity
          const response = await fetch(`${API_BASE_URL}/auth/me`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });

          if (response.ok) {
            const userData = await response.json();
            dispatch({
              type: AUTH_ACTIONS.LOGIN_SUCCESS,
              payload: {
                user: userData.data.user,
                tokens: { accessToken, refreshToken },
              },
            });
          } else {
            // Token invalid, try refresh
            await refreshAccessToken();
          }
        } catch (error) {
          console.error("Auth check failed:", error);
          logout();
        }
      } else {
        // No tokens present, finish initial check
        dispatch({ type: AUTH_ACTIONS.AUTH_CHECK_COMPLETE });
      }
    };

    checkAuthStatus();
  }, [logout, refreshAccessToken]); // Added missing dependencies

  // Login function
  const login = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      const result = await response.json();

      if (response.ok) {
        // Store tokens
        localStorage.setItem("accessToken", result.data.tokens.accessToken);
        localStorage.setItem("refreshToken", result.data.tokens.refreshToken);

        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: {
            user: result.data.user,
            tokens: result.data.tokens,
          },
        });

        // Redirect based on role
        redirectBasedOnRole(result.data.user.role);

        return { success: true };
      } else {
        const errorMessage = result.message || "Login failed";
        dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: errorMessage });
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage = "Network error. Please try again.";
      dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: errorMessage });
      return { success: false, error: errorMessage };
    }
  };

  // Register function (does NOT log the user in or store tokens)
  const register = async (userData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(userData),
      });

      const result = await response.json();

      if (response.ok) {
        // Do not persist any tokens or auth state here
        return { success: true };
      } else {
        const errorMessage = result.message || "Registration failed";
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error("Registration error:", error);
      const errorMessage = "Network error. Please try again.";
      return { success: false, error: errorMessage };
    }
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Redirect based on user role
  const redirectBasedOnRole = (role) => {
    switch (role) {
      case "buyer":
        navigate("/buyer");
        break;
      case "seller":
        navigate("/seller");
        break;
      case "admin":
        navigate("/admin");
        break;
      case "super_admin":
        navigate("/super-admin");
        break;
      default:
        navigate("/login");
    }
  };

  // Role-based utility functions
  const hasRole = (requiredRoles) => {
    if (!state.isAuthenticated || !state.userRole) return false;

    if (Array.isArray(requiredRoles)) {
      return requiredRoles.includes(state.userRole);
    }

    return state.userRole === requiredRoles;
  };

  const isBuyer = () => hasRole(["buyer"]);
  const isSeller = () => hasRole(["seller"]);
  const isAdmin = () => hasRole(["admin"]);
  const isSuperAdmin = () => hasRole(["super_admin"]);

  const value = {
    ...state,
    login,
    register,
    logout,
    refreshAccessToken,
    clearError,
    hasRole,
    isBuyer,
    isSeller,
    isAdmin,
    isSuperAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
